export const ALL_PATIENTS_CRITERIA = 'all_patients';

export const defaultCriteriaRow = {
  criteria: '',
  comparison: '',
  value: '',
  betweenComparison1: '',
  betweenValue1: '',
  betweenComparison2: '',
  betweenValue2: '',
};

export const defaultNormalRangeRow = {
  normalRangeOperator: '',
  normalRangeValue: '',
  normalRangeBetweenComparison1: '',
  normalRangeBetweenValue1: '',
  normalRangeBetweenComparison2: '',
  normalRangeBetweenValue2: '',
};

const criteriaPrefixMap = {
  birth_weight: 'BW',
  age_at_collection: 'COLLECTIONAGE',
  gestational_age: 'GESTATIONAGE',
};

const prefixCriteriaMap = {
  BW: 'birth_weight',
  COLLECTIONAGE: 'age_at_collection',
  GESTATIONAGE: 'gestational_age',
};

const invertOperator = (operator) => {
  switch (operator) {
    case '>': return '<';
    case '>=': return '<=';
    case '<': return '>';
    case '<=': return '>=';
    default: return operator;
  }
};

const operatorToComparison = {
  '>': 'greater_than',
  '>=': 'greater_than_or_equal_to',
  '<': 'less_than',
  '<=': 'less_than_or_equal_to',
};

const comparisonToOperator = {
  greater_than: '>',
  greater_than_or_equal_to: '>=',
  less_than: '<',
  less_than_or_equal_to: '<=',
};

const comparisonToTokenExpression = (comparison, rawValue) => {
  if (rawValue === '' || rawValue === null || rawValue === undefined || !comparison) {
    return null;
  }

  const value = Number(rawValue);
  if (Number.isNaN(value)) {
    return null;
  }

  const operator = comparisonToOperator[comparison];
  if (!operator) {
    return null;
  }

  if (operator === '>' || operator === '>=') {
    return `${value}${invertOperator(operator)}X`;
  }

  return `X${operator}${value}`;
};

export const parseAnalyteCodeString = (codeValue) => {
  if (!codeValue || typeof codeValue !== 'string') {
    return [];
  }

  return codeValue
    .split(',')
    .map(code => code.trim())
    .filter(Boolean);
};

export const joinAnalyteCodes = (codes) => {
  if (!Array.isArray(codes) || !codes.length) {
    return null;
  }

  const filteredCodes = codes.map(code => (typeof code === 'string' ? code.trim() : code)).filter(Boolean);
  return filteredCodes.length ? filteredCodes.join(',') : null;
};

export const getConditionKey = (condition) => {
  if (!condition) return '';
  return String(condition.orphanetCode || condition.acronym || condition.name || condition.OrphanetCode || condition.Acronym || condition.Name || '');
};

// Remove the UI-only `isPendingSave` flag from every condition's cutoff
// profiles. Used before persisting cutoffs and when clearing highlights after a
// successful save, so the flag never reaches the API or lingers in saved state.
export const stripPendingSaveFlags = (conditions) => {
  if (!Array.isArray(conditions)) return conditions;
  return conditions.map((condition) => {
    const profileKey = condition.conditionProfile !== undefined ? 'conditionProfile'
      : (condition.ConditionProfile !== undefined ? 'ConditionProfile' : null);
    if (!profileKey || !Array.isArray(condition[profileKey])) return condition;
    if (!condition[profileKey].some((profile) => profile && profile.isPendingSave)) return condition;
    const nextProfiles = condition[profileKey].map((profile) => {
      if (!profile || !profile.isPendingSave) return profile;
      // eslint-disable-next-line no-unused-vars
      const { isPendingSave: _isPendingSave, ...rest } = profile;
      return rest;
    });
    return { ...condition, [profileKey]: nextProfiles };
  });
};

export const serializePatientCriteria = (rows) => {
  const normalizedRows = Array.isArray(rows) ? rows.filter((row) => !!row?.criteria) : [];
  if (!normalizedRows.length || normalizedRows[0].criteria === ALL_PATIENTS_CRITERIA) {
    return null;
  }

  const tokens = normalizedRows
    .map((row) => {
      const prefix = criteriaPrefixMap[row.criteria];
      if (!prefix) {
        return null;
      }

      if (row.comparison === 'between' && row.criteria === 'birth_weight') {
        const leftOperator = comparisonToOperator[row.betweenComparison1];
        const rightOperator = comparisonToOperator[row.betweenComparison2];
        const leftValue = Number(row.betweenValue1);
        const rightValue = Number(row.betweenValue2);

        if (!leftOperator || !rightOperator || Number.isNaN(leftValue) || Number.isNaN(rightValue)) {
          return null;
        }

        return `${prefix}_${leftValue}${invertOperator(leftOperator)}X${rightOperator}${rightValue}`;
      }

      const expression = comparisonToTokenExpression(row.comparison, row.value);
      if (!expression) {
        return null;
      }

      return `${prefix}_${expression}`;
    })
    .filter(Boolean);

  return tokens.length ? tokens.join('|') : null;
};

export const parsePatientCriteriaString = (patientCriteria) => {
  if (!patientCriteria) {
    return [{ ...defaultCriteriaRow, criteria: ALL_PATIENTS_CRITERIA }];
  }

  const tokens = String(patientCriteria)
    .split('|')
    .map((token) => token.trim())
    .filter(Boolean);

  const parsedRows = [];

  tokens.forEach((token) => {
    const underscoreIndex = token.indexOf('_');
    if (underscoreIndex === -1) {
      return;
    }

    const prefix = token.slice(0, underscoreIndex);
    const expression = token.slice(underscoreIndex + 1);
    const criteria = prefixCriteriaMap[prefix];
    if (!criteria || !expression) {
      return;
    }

    const rangeMatch = expression.match(/^(-?\d*\.?\d+)(>=|>|<=|<)X(>=|>|<=|<)(-?\d*\.?\d+)$/);
    if (rangeMatch) {
      const leftValue = Number(rangeMatch[1]);
      const leftOperator = rangeMatch[2];
      const rightOperator = rangeMatch[3];
      const rightValue = Number(rangeMatch[4]);

      const leftComparison = operatorToComparison[invertOperator(leftOperator)] || '';
      const rightComparison = operatorToComparison[rightOperator] || '';

      if (criteria === 'birth_weight' && leftComparison && rightComparison) {
        parsedRows.push({
          ...defaultCriteriaRow,
          criteria,
          comparison: 'between',
          betweenComparison1: leftComparison,
          betweenValue1: leftValue,
          betweenComparison2: rightComparison,
          betweenValue2: rightValue,
        });
      } else {
        if (leftComparison) {
          parsedRows.push({
            ...defaultCriteriaRow,
            criteria,
            comparison: leftComparison,
            value: leftValue,
          });
        }

        if (rightComparison) {
          parsedRows.push({
            ...defaultCriteriaRow,
            criteria,
            comparison: rightComparison,
            value: rightValue,
          });
        }
      }

      return;
    }

    const xLeadingMatch = expression.match(/^X(>=|>|<=|<)(-?\d*\.?\d+)$/);
    if (xLeadingMatch) {
      const comparison = operatorToComparison[xLeadingMatch[1]];
      if (!comparison) return;
      parsedRows.push({
        ...defaultCriteriaRow,
        criteria,
        comparison,
        value: Number(xLeadingMatch[2]),
      });
      return;
    }

    const xTrailingMatch = expression.match(/^(-?\d*\.?\d+)(>=|>|<=|<)X$/);
    if (xTrailingMatch) {
      const comparison = operatorToComparison[invertOperator(xTrailingMatch[2])];
      if (!comparison) return;
      parsedRows.push({
        ...defaultCriteriaRow,
        criteria,
        comparison,
        value: Number(xTrailingMatch[1]),
      });
    }
  });

  if (!parsedRows.length) {
    return [{ ...defaultCriteriaRow, criteria: ALL_PATIENTS_CRITERIA }];
  }

  return parsedRows;
};

export const normalizePatientCriteriaValue = (criteria) => {
  if (!criteria || typeof criteria !== 'string') {
    return ALL_PATIENTS_CRITERIA;
  }

  return criteria.toLowerCase();
};

export const getNormalRangeRowsFromCutoff = (cutoff) => {
  if (Array.isArray(cutoff?.normalRangeRows) && cutoff.normalRangeRows.length) {
    return cutoff.normalRangeRows.map((row) => ({ ...defaultNormalRangeRow, ...row }));
  }

  const rows = [];
  const floorValue = cutoff?.normalFloor;
  const ceilingValue = cutoff?.normalCeiling;

  if (floorValue !== undefined && floorValue !== null) {
    const floorOperator = cutoff?.normalFloorCondition === 'GREATER_OR_EQUAL'
      ? 'greater_than_or_equal_to'
      : 'greater_than';
    rows.push({
      ...defaultNormalRangeRow,
      normalRangeOperator: floorOperator,
      normalRangeValue: floorValue,
    });
  }

  if (ceilingValue !== undefined && ceilingValue !== null) {
    const ceilingOperator = cutoff?.normalCeilingCondition === 'LESS_OR_EQUAL'
      ? 'less_than_or_equal_to'
      : 'less_than';
    rows.push({
      ...defaultNormalRangeRow,
      normalRangeOperator: ceilingOperator,
      normalRangeValue: ceilingValue,
    });
  }

  if (rows.length) {
    return rows;
  }

  if (cutoff?.normalRangeOperator && cutoff?.normalRangeValue !== undefined && cutoff?.normalRangeValue !== null) {
    return [{
      ...defaultNormalRangeRow,
      normalRangeOperator: cutoff.normalRangeOperator,
      normalRangeValue: cutoff.normalRangeValue,
    }];
  }

  return [{
    ...defaultNormalRangeRow,
    normalRangeValue: cutoff?.normalRangeValue ?? '',
  }];
};

export const isComparisonAllowedForCriteria = (criteria, comparison) => {
  if (!criteria || !comparison) return true;

  if (criteria === 'birth_weight') {
    return [
      'less_than',
      'less_than_or_equal_to',
      'greater_than',
      'greater_than_or_equal_to',
      'between',
    ].includes(comparison);
  }

  if (criteria === 'age_at_collection' || criteria === 'gestational_age') {
    return [
      'less_than',
      'less_than_or_equal_to',
      'greater_than',
      'greater_than_or_equal_to',
      'between',
    ].includes(comparison);
  }

  return false;
};

export const comparisonToConstraint = (comparison, rawValue) => {
  const value = Number(rawValue);
  if (Number.isNaN(value)) return null;

  switch (comparison) {
    case 'less_than':
      return { lower: null, lowerInclusive: true, upper: value, upperInclusive: false };
    case 'less_than_or_equal_to':
      return { lower: null, lowerInclusive: true, upper: value, upperInclusive: true };
    case 'greater_than':
      return { lower: value, lowerInclusive: false, upper: null, upperInclusive: true };
    case 'greater_than_or_equal_to':
      return { lower: value, lowerInclusive: true, upper: null, upperInclusive: true };
    default:
      return null;
  }
};

export const mergeConstraints = (left, right) => {
  const lowerCandidates = [left.lower, right.lower].filter(v => v !== null && v !== undefined);
  const upperCandidates = [left.upper, right.upper].filter(v => v !== null && v !== undefined);

  const merged = {
    lower: lowerCandidates.length ? Math.max(...lowerCandidates) : null,
    lowerInclusive: true,
    upper: upperCandidates.length ? Math.min(...upperCandidates) : null,
    upperInclusive: true,
  };

  if (merged.lower !== null) {
    const lowerFlags = [];
    if (left.lower === merged.lower) lowerFlags.push(left.lowerInclusive);
    if (right.lower === merged.lower) lowerFlags.push(right.lowerInclusive);
    merged.lowerInclusive = lowerFlags.every(Boolean);
  }

  if (merged.upper !== null) {
    const upperFlags = [];
    if (left.upper === merged.upper) upperFlags.push(left.upperInclusive);
    if (right.upper === merged.upper) upperFlags.push(right.upperInclusive);
    merged.upperInclusive = upperFlags.every(Boolean);
  }

  return merged;
};

export const isConstraintValid = (constraint) => {
  if (constraint.lower === null || constraint.upper === null) return true;
  if (constraint.lower < constraint.upper) return true;
  if (constraint.lower > constraint.upper) return false;
  return constraint.lowerInclusive && constraint.upperInclusive;
};

// --- New format utilities (BirthWeight, CollectionAge, GestationAge, NormalRange keys) ---

const criteriaKeyMap = {
  birth_weight: 'birthWeight',
  age_at_collection: 'collectionAge',
  gestational_age: 'gestationAge',
};

const criteriaKeyReverseMap = {
  birthWeight: 'birth_weight',
  collectionAge: 'age_at_collection',
  gestationAge: 'gestational_age',
};

export const CRITERIA_KEYS = ['birthWeight', 'collectionAge', 'gestationAge'];

export const parseRangeString = (rangeStr) => {
  if (!rangeStr || typeof rangeStr !== 'string') return null;
  const trimmed = rangeStr.trim();

  // Full range: value1 op1 X op2 value2
  const fullMatch = trimmed.match(/^(-?\d*\.?\d+)(<=|<|>=|>)X(<=|<|>=|>)(-?\d*\.?\d+)$/);
  if (fullMatch) {
    return {
      lowerValue: Number(fullMatch[1]),
      lowerOp: fullMatch[2],
      upperOp: fullMatch[3],
      upperValue: Number(fullMatch[4]),
    };
  }

  // Left only: value op X
  const leftMatch = trimmed.match(/^(-?\d*\.?\d+)(<=|<|>=|>)X$/);
  if (leftMatch) {
    return {
      lowerValue: Number(leftMatch[1]),
      lowerOp: leftMatch[2],
      upperOp: null,
      upperValue: null,
    };
  }

  // Right only: X op value
  const rightMatch = trimmed.match(/^X(<=|<|>=|>)(-?\d*\.?\d+)$/);
  if (rightMatch) {
    return {
      lowerValue: null,
      lowerOp: null,
      upperOp: rightMatch[1],
      upperValue: Number(rightMatch[2]),
    };
  }

  return null;
};

export const buildRangeString = (parts) => {
  let lowerPart = null;
  let upperPart = null;

  (Array.isArray(parts) ? parts : []).forEach(({ comparison, value }) => {
    if (value === '' || value === null || value === undefined) return;
    const numValue = Number(value);
    if (Number.isNaN(numValue)) return;

    if (comparison === 'greater_than') {
      lowerPart = `${numValue}<`;
    } else if (comparison === 'greater_than_or_equal_to') {
      lowerPart = `${numValue}<=`;
    } else if (comparison === 'less_than') {
      upperPart = `<${numValue}`;
    } else if (comparison === 'less_than_or_equal_to') {
      upperPart = `<=${numValue}`;
    }
  });

  if (lowerPart && upperPart) return `${lowerPart}X${upperPart}`;
  if (lowerPart) return `${lowerPart}X`;
  if (upperPart) return `X${upperPart}`;
  return null;
};

const rangeStringToCriteriaRow = (criteria, rangeStr) => {
  const parsed = parseRangeString(rangeStr);
  if (!parsed) return null;

  if (parsed.lowerValue !== null && parsed.upperValue !== null) {
    const comp1 = operatorToComparison[invertOperator(parsed.lowerOp)];
    const comp2 = operatorToComparison[parsed.upperOp];
    return {
      ...defaultCriteriaRow,
      criteria,
      comparison: 'between',
      betweenComparison1: comp1 || '',
      betweenValue1: parsed.lowerValue,
      betweenComparison2: comp2 || '',
      betweenValue2: parsed.upperValue,
    };
  }

  if (parsed.lowerValue !== null) {
    const comp = operatorToComparison[invertOperator(parsed.lowerOp)];
    return {
      ...defaultCriteriaRow,
      criteria,
      comparison: comp || '',
      value: parsed.lowerValue,
    };
  }

  if (parsed.upperValue !== null) {
    const comp = operatorToComparison[parsed.upperOp];
    return {
      ...defaultCriteriaRow,
      criteria,
      comparison: comp || '',
      value: parsed.upperValue,
    };
  }

  return null;
};

export const parseCriteriaFromCutoff = (cutoff) => {
  const rows = [];

  CRITERIA_KEYS.forEach((key) => {
    const rangeStr = cutoff?.[key];
    if (!rangeStr) return;
    const criteria = criteriaKeyReverseMap[key];
    if (!criteria) return;
    const row = rangeStringToCriteriaRow(criteria, rangeStr);
    if (row) rows.push(row);
  });

  if (rows.length === 0) {
    return [{ ...defaultCriteriaRow, criteria: ALL_PATIENTS_CRITERIA }];
  }

  return rows;
};

export const serializeCriteriaToKeys = (criteriaRows) => {
  const result = { birthWeight: null, collectionAge: null, gestationAge: null };
  const normalizedRows = Array.isArray(criteriaRows) ? criteriaRows.filter((row) => !!row?.criteria) : [];
  if (!normalizedRows.length || normalizedRows[0].criteria === ALL_PATIENTS_CRITERIA) {
    return result;
  }

  normalizedRows.forEach((row) => {
    const key = criteriaKeyMap[row.criteria];
    if (!key) return;

    let parts;
    if (row.comparison === 'between') {
      parts = [
        { comparison: row.betweenComparison1, value: row.betweenValue1 },
        { comparison: row.betweenComparison2, value: row.betweenValue2 },
      ];
    } else {
      parts = [{ comparison: row.comparison, value: row.value }];
    }

    result[key] = buildRangeString(parts);
  });

  return result;
};

export const parseNormalRangeFromCutoff = (cutoff) => {
  const rangeStr = cutoff?.normalRange;
  if (rangeStr && typeof rangeStr === 'string') {
    const parsed = parseRangeString(rangeStr);
    if (parsed) {
      if (parsed.lowerValue !== null && parsed.upperValue !== null) {
        // Full range → single between row
        const comp1 = operatorToComparison[invertOperator(parsed.lowerOp)];
        const comp2 = operatorToComparison[parsed.upperOp];
        return [{
          ...defaultNormalRangeRow,
          normalRangeOperator: 'between',
          normalRangeBetweenComparison1: comp1 || '',
          normalRangeBetweenValue1: parsed.lowerValue,
          normalRangeBetweenComparison2: comp2 || '',
          normalRangeBetweenValue2: parsed.upperValue,
        }];
      }
      if (parsed.lowerValue !== null) {
        const comp = operatorToComparison[invertOperator(parsed.lowerOp)];
        if (comp) return [{ ...defaultNormalRangeRow, normalRangeOperator: comp, normalRangeValue: parsed.lowerValue }];
      }
      if (parsed.upperValue !== null) {
        const comp = operatorToComparison[parsed.upperOp];
        if (comp) return [{ ...defaultNormalRangeRow, normalRangeOperator: comp, normalRangeValue: parsed.upperValue }];
      }
    }
  }

  // Fall back to legacy format, combining floor+ceiling into a between row if both present
  const legacyRows = getNormalRangeRowsFromCutoff(cutoff);
  if (legacyRows.length >= 2) {
    const lowerRow = legacyRows.find(r => ['greater_than', 'greater_than_or_equal_to'].includes(r.normalRangeOperator));
    const upperRow = legacyRows.find(r => ['less_than', 'less_than_or_equal_to'].includes(r.normalRangeOperator));
    if (lowerRow && upperRow) {
      return [{
        ...defaultNormalRangeRow,
        normalRangeOperator: 'between',
        normalRangeBetweenComparison1: lowerRow.normalRangeOperator,
        normalRangeBetweenValue1: lowerRow.normalRangeValue,
        normalRangeBetweenComparison2: upperRow.normalRangeOperator,
        normalRangeBetweenValue2: upperRow.normalRangeValue,
      }];
    }
  }
  return [legacyRows[0] ? { ...defaultNormalRangeRow, ...legacyRows[0] } : { ...defaultNormalRangeRow }];
};

export const serializeNormalRangeKey = (normalRangeRows) => {
  const row = Array.isArray(normalRangeRows) ? normalRangeRows[0] : null;
  if (!row) return null;

  if (row.normalRangeOperator === 'between') {
    return buildRangeString([
      { comparison: row.normalRangeBetweenComparison1, value: row.normalRangeBetweenValue1 },
      { comparison: row.normalRangeBetweenComparison2, value: row.normalRangeBetweenValue2 },
    ]);
  }

  if (!row.normalRangeOperator || row.normalRangeValue === '' || row.normalRangeValue === null || row.normalRangeValue === undefined) {
    return null;
  }

  return buildRangeString([{ comparison: row.normalRangeOperator, value: row.normalRangeValue }]);
};